import {ptlEnvs} from "@/tra/tancis/ptli/comn";
import {api} from "@/comn";
import {comnUtils} from "@/comn/utils";
import {TFormSchema, TGridSchema} from "@/comn/hooks";


/**
 * PtliReviewProcessService
 * ------------------------
 * Provides constants, API calls, and form/grid schemas for managing review processes.
 *
 * BASE:
 * - Defines base path, API URL, and navigation nodes.
 *
 * URLS:
 * - Predefined URLs for list, search, and detail pages.
 *
 * APIS:
 * - getRevwPrcssList: fetch paginated list of review processes
 * - getRevwPrcss: fetch single review process by ID
 * - saveRevwPrcss: create a new review process
 * - updateRevwPrcss: update an existing review process by ID
 * - deleteRevwPrcss: delete a review process by ID
 *
 * SF_REVW_PRCSS_SRCH:
 * - Form schema for searching review processes by ID or name.
 *
 * SF_REVW_PRCSS:
 * - Main form schema for creating/updating a review process.
 * - Fields include ID (read-only), name, classification code, label type, and registration info.
 *
 * SG_REVW_PRCSS_LIST:
 * - Grid schema for displaying review process list.
 * - Columns include process ID, name, classification, first registrar, and registration date.
 */

export const BASE = {
    path: `${ptlEnvs.base}/ptl/revwprcs`,
    api: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/ptl/revw/prcss`,
    nodes: [
        { path: "/", label: "L_LBL_MGMT" },
        { path: "/ptl/revwprcs", label: "L_LBL_MGMT" },
    ],
};

export const URLS = {
    ptli0707001q: `${BASE.path}/ptli0707001q`,
    ptli0707002s: `${BASE.path}/ptli0707002s`,
    ptli0707003s: `${BASE.path}/ptli0707003s`,
};

export const APIS = {
    // Fetch Review Process list with pagination
    getRevwPrcssList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },

    // Fetch a single Review Process by ID
    getRevwPrcss: (id: any) => {
        return api.get(`${BASE.api}/${id}`);
    },

    // Save a new Review Process
    saveRevwPrcss: (data: any) => {
        return api.post(`${BASE.api}`, {
            ...data,
        });
    },

    // Update an existing Review Process
    updateRevwPrcss: (data: any, id: any) => {
        return api.put(`${BASE.api}/${id}`, {
            ...data,
        });
    },

    // Delete a Review Process by ID
    deleteRevwPrcss: (id: any) => {
        return api.delete(`${BASE.api}/${id}`);
    },
};

// Search form schema
export const SF_REVW_PRCSS_SRCH: TFormSchema = {
    id: "form_RevwPrcssSrch",
    schema: {
        usrFdbckPrcssId: { type: "text", label: "L_REVWPRCSS_ID"},
        usrFdbckPrcssNm: { type: "text", label: "L_REVWPRCSS_NM" },
        // bsopClsfCd: { type: "select", label: "L_BSOP_CLSF_CD", all: true, area: "comnCd", comnCd: "COM0024" },
    },
};

// Main form schema for creating/updating Review Process
export const SF_REVW_PRCSS: TFormSchema = {
    id: "form_RevwPrcss",
    schema: {
        usrFdbckPrcssId: {
            type: "text",
            label: "L_REVWPRCSS_ID",
            readOnly: true,
            placeholder: "This is auto generated"
        },
        usrFdbckPrcssNm: { type: "text", label: "L_REVWPRCSS_NM", required: true }, // Name is required
        bsopClsfCd: {
            type: "select",
            label: "L_BSOP_CLSF_CD",
            required: true,
            all: true,
            area: "comnCd",
            comnCd: "COM0024",
        },
        lblTpCd: {
            type: "select",
            label: "L_LBL_TP_CD",
            select: false,
            required: true,
            options: [
                { label: "Label", value: "L" },
                { label: "Title", value: "T" },
                { label: "Button", value: "B" },
            ],
        },
        frstRegstId: { type: "text", label: "L_FRST_REGST_ID" },
        frstRgsrDtm: { type: "text", label: "L_FRST_RGSR_DTM" },
    },
};

// Grid schema for displaying Review Process list
export const SG_REVW_PRCSS_LIST: TGridSchema = {
    id: "grid_RevwPrcssList",
    options: { pagination: "out", checkbox: true }, // Uses external pagination + checkboxes
    head: [
        { cells: [{ header: "L_REVWPRCSS_ID", binding: "usrFdbckPrcssId", width: "*" }] },
        { cells: [{ header: "L_REVWPRCSS_NM", binding: "usrFdbckPrcssNm", width: "*" }] },
        { cells: [{ header: "L_BSOP_CLSF_CD", binding: "bsopClsfCd", width: "*" }] },
        { cells: [{ header: "L_FRST_REGST_ID", binding: "frstRegstId", width: "10%" }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: "15%" }] },
    ],
    body: [
        { cells: [{ binding: "usrFdbckPrcssId" }] },
        { cells: [{ binding: "usrFdbckPrcssNm" }] },
        { cells: [{ binding: "bsopClsfCd" }] },
        { cells: [{ binding: "frstRegstId" }] },
        { cells: [{ binding: "frstRgsrDtm", type: "datetime" }] },
    ],
};

export {}
